import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import {Component} from 'react';
import {connect} from 'react-redux';
import Language from '../../../../v2/lang/language';
import attributes from '../../../../v2/tracking/attributes.js';
import doMetric from '../../../../v2/tracking/bigQueryMetricRecorder.js';
import Track from '../../../../v2/utils/tracking';
import {getMobileOSName} from '../../../../v2/utils/user-agent';
import CopyToClipboard from '../CopyToClipBoard/CopyToClipboard.jsx';
import MoreOrLessButton from '../MoreOrLessButton/MoreOrLessButton.jsx';
import SocialChannelButton from '../SocialSharingButton/SocialChannelButton.jsx';
import getChannelAction from '../utils/get-channel-action';
import getChannelInfo from '../utils/get-channel-info';

const THROTTLE_HANDLE_CONTENT_FOR_WINDOW_SIZE = 500;
const HEIGHT_OF_TWO_ROWS = 100;

class SocialChannelButtonGroup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isExpanded: false,
      showCopyToClipboardPopup: false,
      hasExpandedContent: false,
      hasOverflowingContent: false,
    };
    this.handleContentForWindowSize = this.handleContentForWindowSize.bind(this);
    this.handleResize = throttle(this.handleContentForWindowSize.bind(this), THROTTLE_HANDLE_CONTENT_FOR_WINDOW_SIZE);
    this.toggleExpand = this.toggleExpand.bind(this);
    this.toggleShowCopyToClipboardPopup = this.toggleShowCopyToClipboardPopup.bind(this);
    this.setExpandableSection = this.setExpandableSection.bind(this);
    this.attribute = [attributes.getAttribute('channelName', 'COPY')];
    this.bqEventSubtype = 'clickSocialSharing';
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    setTimeout(this.handleContentForWindowSize, THROTTLE_HANDLE_CONTENT_FOR_WINDOW_SIZE);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  componentDidUpdate() {
    this.handleContentForWindowSize();
  }

  toggleExpand() {
    this.setState({
      ...this.state,
      isExpanded: !this.state.isExpanded,
    });
  }

  toggleShowCopyToClipboardPopup() {
    this.setState({
      ...this.state,
      showCopyToClipboardPopup: !this.state.showCopyToClipboardPopup,
    });
    try {
      doMetric(this.bqEventSubtype, this.attribute);
    } catch (error) {
      Track.track(error);
    }
  }

  handleContentForWindowSize() {
    const hasOverflowingContent = this.expandableSection.offsetHeight < this.expandableSection.scrollHeight;
    const hasExpandedContent = this.expandableSection.offsetHeight > HEIGHT_OF_TWO_ROWS;

    if (hasOverflowingContent !== this.state.hasOverflowingContent
      || hasExpandedContent !== this.state.hasExpandedContent) {
      this.setState({...this.state, hasOverflowingContent, hasExpandedContent});
    }
  }

  setExpandableSection(el) {
    this.expandableSection = el;
  }

  renderSocialChannelButtons() {
    const {props} = this;
    return props.socialChannels.map((socialChannel, index) => {
      const channelInfo = getChannelInfo(socialChannel.channel);
      const channelName = channelInfo.name;
      if (
        (!channelName || (!getMobileOSName() && channelInfo.mobileOnly))
        || (props.isSocialSharingRestricted && channelInfo.restrict)
      ) {
        return null;
      }
      const action = getChannelAction({
        socialChannel,
        message: Language.getText(`theme.${props.siteCode}.referralsSocialSharing.message`) || Language.getText('referralsSocialSharing.message'),
        inwardReferralUrl: props.inwardReferralUrl,
        inwardSiteUrl: props.inwardReferralUrl,
      });
      return <SocialChannelButton key={index} channelName={channelName} icon={channelInfo.icon} action={action} />;
    });
  }

  render() {
    const {props} = this;
    const expandedToggle = this.state.isExpanded ? 'referralsSocialSharing_expandableSection-expanded' : '';
    const copyLink = 'COPY_LINK';
    const copyLinkInfo = getChannelInfo(copyLink);
    return ([
      <div
        key="1"
        ref={this.setExpandableSection}
        className={`referralsSocialSharing_expandableSection ${expandedToggle}`}
      >
        <div className="referralsSocialSharing_socialButtons">
          {this.renderSocialChannelButtons()}
          <SocialChannelButton
            channelName={copyLinkInfo.name}
            icon={copyLinkInfo.icon}
            action={this.toggleShowCopyToClipboardPopup}
          />
        </div>
      </div>,
      <MoreOrLessButton
        key="2"
        toggleExpand={this.toggleExpand}
        isExpanded={this.state.isExpanded}
        hasExpandedContent={this.state.hasExpandedContent}
        hasOverflowingContent={this.state.hasOverflowingContent}
      />,
      <CopyToClipboard
        key="3"
        value={`${props.inwardReferralUrl}&li`}
        show={this.state.showCopyToClipboardPopup}
        onClose={this.toggleShowCopyToClipboardPopup}
      />,
    ]);
  }
}

const mapStateToProps = state => (
  {
    socialChannels: state.socialSharing.socialChannels,
    inwardReferralUrl: state.socialSharing.inwardReferralUrl,
    inwardSiteUrl: state.socialSharing.inwardSiteUrl,
    isSocialSharingRestricted: state.socialSharing.isSocialSharingRestricted,
    siteCode: state.site.siteCode,
  }
);

SocialChannelButtonGroup.propTypes = {
  socialChannels: PropTypes.array.isRequired,
  inwardReferralUrl: PropTypes.string.isRequired,
  inwardSiteUrl: PropTypes.string.isRequired,
  siteCode: PropTypes.string,
  isSocialSharingRestricted: PropTypes.bool.isRequired,
};

export default connect(
  mapStateToProps,
)(SocialChannelButtonGroup);
